import { Component, OnDestroy, ViewChild } from '@angular/core';
import { Safety, SafetyCategoryReading, SafetyReport, SafetyWeeklySummary } from '../../../../models/safety/safety.models';
import { PtLuxonDate } from '../../../../models/projects/date.models';
import { Events, ModalController, NavController, NavParams } from 'ionic-angular';
import { LockedDayService } from '../../../../services/locked-day/locked-day.service';
import { DateService } from '../../../../services/date/date.service';
import { DailyLogService } from '../../../../services/daily-log/daily-log.service';
import { SafetyService } from '../../../../services/safety/safety.service';
import { SafetyReportEditPage } from '../safety-report-edit/safety-report-edit';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { LockDayFilters } from '../../../../models/lock-day.models';
import {
  ActionType,
  ErrorType,
  ValidationError,
  PageValidationType,
  ValidationResult
} from '../../../../models/projects/validation.models';
import { catchError, debounce } from 'rxjs/operators';
import { timer } from 'rxjs/observable/timer';
import { LockedDayPermissions, SafetyPermissions } from '../../../../models/permissions/permission.models';
import { PermissionsService } from '../../../../services/permissions/permissions.service';
import { CompleteDayService } from '../../../../services/complete-day/complete-day.service';
import { NavHelperService } from '../../../../services/nav/nav-helper.service';
import { cloneDeep } from 'lodash';
import { ImplementerService } from '../../../../services/implementer/implementer.service';
import { DailyLogDatePickerComponent } from '../../../shared/components/daily-log-date-picker/daily-log-date-picker';
import { ConfirmationModalComponent } from '../../../daily-log/confirmation-modal/confirmation-modal.component';
import { AppConfig } from '../../../../app/app.config';

@Component({
  selector: 'app-safety-edit-component',
  templateUrl: 'safety-edit-component.component.html'
})
export class SafetyEditComponentComponent implements OnDestroy {
  @ViewChild('dailyLogDatePickerComponent') dailyLogDatePickerComponent: DailyLogDatePickerComponent;

  config = AppConfig.settings;
  safetyModel: Safety = new Safety();
  safetyModelClone: Safety = new Safety();

  weeklyModel: SafetyWeeklySummary = new SafetyWeeklySummary();
  dailyLogDate: PtLuxonDate = new PtLuxonDate();
  validationResult: ValidationResult = new ValidationResult();

  $saveRequestedChange: Subject<boolean> = new Subject<boolean>();
  saveDebounceSubscription: Subscription;

  loading: boolean;
  validationLoading: boolean;
  currentSetupConfigured: boolean;
  private savingSafety: boolean = false;
  private pendingSafetySave: boolean = false;

  dayLockedByDate: boolean = false;
  lockable: boolean = false;
  dayCompleted: boolean;
  isImplementerActiveForDay: boolean; // when true the day will be open for editing regardless of dayLockedByDate

  totalSafetyCategory: number;

  firstLoadComplete: boolean = false;

  permissions: SafetyPermissions = new SafetyPermissions();
  lockedDayPermissions: LockedDayPermissions = new LockedDayPermissions();
  dateBeforeInitialSetup: boolean;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public modalController: ModalController,
    public lockedDayService: LockedDayService,
    public dateService: DateService,
    public dailyLogService: DailyLogService,
    public safetyService: SafetyService,
    public events: Events,
    public permissionService: PermissionsService,
    private completeDayService: CompleteDayService,
    private navHelperService: NavHelperService,
    private implementerService: ImplementerService,
  ) {
    this.events.subscribe('saveSafety', async () => {
      await this.saveSafetyData(false);
    });

    this.navHelperService.currentSetupConfigured.subscribe(currentSetupConfigured => {
      this.currentSetupConfigured = currentSetupConfigured;
    });
  }

  async ngOnInit() {
    this.loading = true;
    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
    await this.ionViewEnter();

    await this.updateDataEntryVariables();
  }

  async ionViewEnter() {
    await this.loadData(true);
    this.setupAutoSaveDebounce();

    this.permissions = await this.permissionService.safetyPermissions();
    this.lockedDayPermissions = await this.permissionService.lockedDayPermissions(this.dailyLogDate);
  }

  ngOnDestroy(): void {
    // without unsubscribing to this, old values that were published previously will still be held, causing errors if those values no longer exist
    this.events.unsubscribe('saveSafety');
    if (this.saveDebounceSubscription) {
      this.saveDebounceSubscription.unsubscribe();
    }
  }

  // one method for all - that checks dayCompleted, implementer status, etc, and sets canEnterData flag
  async updateDataEntryVariables() {
    await Promise.all([
      this.checkImplementerActive(),
      this.checkDayLocked(),
      this.permissionService.updateLockedDayFunctionalPermissions(this.lockedDayPermissions, this.dailyLogDate)
    ]);

    await this.setCanEnterDataFlag();
  }

  dateBeforeInitialSetupChanged($event: boolean) {
    this.dateBeforeInitialSetup = $event;
  }

  async setCanEnterDataFlag() {
    if (!this.dayCompleted
      && ((this.config.isImplementerEnvironment && this.isImplementerActiveForDay) || (!this.config.isImplementerEnvironment && this.dayLockedByDate))) {
      this.navHelperService.updateCanEnterData(true);
    } else {
      this.navHelperService.updateCanEnterData(false);
    }
  }

  setupAutoSaveDebounce() {
    if (this.saveDebounceSubscription) {
      this.saveDebounceSubscription.unsubscribe();
    }

    this.saveDebounceSubscription = this.$saveRequestedChange
      .pipe(
        catchError(err => {
          throw (err.error);
        }),
        debounce(() => timer(500)))
      .subscribe(val => {
        this.saveSafetyData(val);
      });
  }

  async checkDayLocked() {
    const filters: LockDayFilters = {
      date: this.dailyLogDate.dateTime,
      userId: null,
      history: null
    };

    const lockedSummary = await this.lockedDayService.checkDay(filters);

    this.dayLockedByDate = lockedSummary.lockedByDate;
    this.lockable = lockedSummary.lockable;

    this.completeDayService.checkDay(filters).then(result => {
      this.dayCompleted = result;
    });
  }

  async dayLockedForEditing() {
    this.checkDayLocked();
  }

  async changesCancelled() {
    this.firstLoadComplete = false;
    this.loading = true;
    await this.loadData(true);
  }

  async changesSaved() {
    await this.loadData(false);
  }

  queueAutoSave(isComplete: boolean) {
    if (this.loading || !this.firstLoadComplete || this.dailyLogDatePickerComponent?.dateChangeLoading) {
      return;
    }

    this.validationLoading = true;
    this.$saveRequestedChange.next(isComplete);
  }

  public async saveSafetyData(isComplete: boolean) {
    if (this.savingSafety) {
      this.pendingSafetySave = true;
      return;
    }

    this.validationLoading = true;
    this.savingSafety = true;

    if ((!this.config.isImplementerEnvironment && !this.dayLockedByDate)
      || ((this.config.isImplementerEnvironment && !this.isImplementerActiveForDay))) {
      this.validationLoading = false;
      this.savingSafety = false;
      return;
    }

    try {
      await this.updateWeeklyData();
      const editorToSave = cloneDeep(this.safetyModel);
      editorToSave.date = cloneDeep(this.dailyLogDate);
      editorToSave.isCompleted = isComplete;

      const savedEditor = await this.safetyService.saveEditor(editorToSave);
      this.safetyModel.id = savedEditor.id;
      this.safetyModel.isCompleted = isComplete;
      this.validationResult = new ValidationResult();
    } catch (error) {
      if (this.isValidationSaveError(error)) {
        this.validationResult = new ValidationResult();
        this.applyValidationErrors(error);
        this.safetyModel.isCompleted = false;
      } else {
        throw error;
      }
    } finally {
      this.validationLoading = false;
      this.savingSafety = false;

      if (this.pendingSafetySave) {
        this.pendingSafetySave = false;
        await this.saveSafetyData(false);
      }
    }
  }

  markSafetyComplete() {
    this.saveSafetyData(true);
  }

  async loadData(refresh: boolean, dateChange?: boolean) {
    if (refresh) {
      this.loading = true;
    }

    // refresh daily log date, if user has changed the date
    if (dateChange) {
      this.firstLoadComplete = false;
      this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
      await this.updateDataEntryVariables();
    }

    if (refresh) {
      this.safetyModel = new Safety();
      this.safetyModel.date = this.dailyLogDate;
    }

    await this.checkDayLocked();

    if (refresh) {
      this.validationResult = new ValidationResult();
      this.safetyModel = await this.safetyService.getSafetyEditorForDay(this.dailyLogDate);
      this.safetyModelClone = cloneDeep(this.safetyModel);

      await this.updateWeeklyData();
      this.firstLoadComplete = true;
      this.loading = false;
      this.validationLoading = true;
      this.safetyService.validate(this.safetyModel).subscribe(() => {
          this.validationLoading = false;
        },
        error => {
          if (this.isValidationSaveError(error)) {
            this.applyValidationErrors(error);
            this.safetyModel.isCompleted = false;
            this.validationLoading = false;
          }
        });
    } else {
      await this.updateWeeklyData();
      this.loading = false;
    }
  }

  async updateWeeklyData() {
    this.safetyModel = this.safetyService.updateSafetyTotals(this.safetyModelClone, this.safetyModel);
    this.safetyModelClone = cloneDeep(this.safetyModel);

    this.updateCategoryTotal();
  }

  updateCategoryTotal() {
    this.totalSafetyCategory = 0;

    this.safetyModel.readings.forEach(value => {
      this.totalSafetyCategory = this.totalSafetyCategory + +value.value;
    });
  }

  getProjectSummary(record: SafetyCategoryReading): number {
    const summary = this.weeklyModel.readingPtdSummaries
      .filter(value => value.category.id === record.category.id);

    if (summary && summary.length > 0) {
      return summary[0].value;
    }

    return 0;
  }

  getYearlySummary(record: SafetyCategoryReading): number {
    const summary = this.weeklyModel.readingYtdSummaries
      .filter(value => value.category.id === record.category.id);

    if (summary && summary.length > 0) {
      return summary[0].value;
    }

    return 0;
  }

  editReport(editor: SafetyReport) {
    this.navCtrl.push(SafetyReportEditPage, {
      editor: editor,
      model: this.safetyModel,
      isCreate: false
    });
  }

  async openDeleteConfirmationModel(editor: SafetyReport) {
    const confirmationModal = this.modalController.create(ConfirmationModalComponent, {
      title: 'Confirm Removal',
      item: editor.safetyCategory.title,
      message: 'Are you sure you want to remove this ' + editor.safetyCategory.title + ' report?',
      optionalMessage: 'This action cannot be undone.',
      trueText: 'Confirm',
      falseText: 'Cancel',
      showButtons: true
    }, {cssClass: 'daily-log-modal-two-line'});

    confirmationModal.onDidDismiss(async data => {
      if (data && data.choice) {
        await this.deleteReport(editor);
      }
    });

    confirmationModal.present();
  }

  async deleteReport(editor: SafetyReport) {
    const index = this.safetyModel.reports.findIndex(e => e.id === editor.id);
    this.safetyModel.reports.splice(index, 1);
    await this.saveSafetyData(false);
  }

  addReport() {
    const editor = new SafetyReport();
    this.navCtrl.push(SafetyReportEditPage, {
      editor: editor,
      model: this.safetyModel,
      isCreate: true
    });
  }

  otherPagesNotComplete(): boolean {
    if (this.validationResult.errors.some(e => e.pageValidationType != null
      && e.pageValidationType !== PageValidationType.DailyObservations
      && e.pageValidationType !== PageValidationType.FieldPositioning
      && e.pageValidationType !== PageValidationType.VoyageLog)) {
      return true;
    }

    return false;
  }

  // isImplementerEnvironment variable only tells us if the environment is the implementer, we still need to check if current date falls between
  // snapshotSummary start and end dates, if it does then we know this date is editable
  async checkImplementerActive() {
    this.isImplementerActiveForDay = await this.implementerService.implementerIsActive(this.config.isVesselEnvironment, cloneDeep(this.dailyLogDate));
  }

  goToActiveLogDay() {
    this.dailyLogDatePickerComponent.goToActiveLogDay();
  }

  dateLoadingStarted() {
    this.firstLoadComplete = false;
  }

  fieldRowClasses(field: string) {
    return {
      'validation-failed': this.hasValidationError(field)
    };
  }

  private isValidationSaveError(error: any): boolean {
    return (error?.status === 412 || error?.status === 400) && !!error?.error?.errors?.length;
  }

  private applyValidationErrors(error: any) {
    const errors = (error?.error?.errors || []) as ValidationError[];

    this.validationResult.errors = this.validationResult.errors.concat(
      errors.filter(p =>
        this.validationResult.errors.filter(er =>
          er.message === p.message).length === 0));

    this.validationResult.errors.forEach(hseqError => {
      if (hseqError.pageValidationType == null) {
        hseqError.pageValidationType = PageValidationType.Safety;
        hseqError.type = ErrorType.Error;
        hseqError.actionType = ActionType.Safety;
      }
    });

    this.validationResult.isValid = error?.error?.isValid === undefined ? false : error.error.isValid;
  }

  private hasValidationError(field: string): boolean {
    const expectedProperty = this.normalizeValidationValue(field);

    return (this.validationResult?.errors || []).some(error =>
      this.normalizeValidationValue(error?.property) === expectedProperty);
  }

  private normalizeValidationValue(value: string): string {
    return (value || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
  }
}



// WEBPACK FOOTER //
// ./src/pages/DPR Pages/safety/safety-edit-component/safety-edit-component.component.ts